import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { NJButton, NJFormItem, NJIconButton, NJTab, NJText, NJToastService, NJTooltip } from '@engie-group/fluid-design-system-react';
import { ENERGIES, USAGES, fmt, isValidRatio, missingRatioCount, periodsOf, referencePeriodOf, setUsageRatio } from './domain';
import { isValidPerf, perfOf, setPerf } from './perf';
import { parseNum } from './sheet';
import { copyRatioColumn, copyRatioTable, pasteRatioColumn, pasteRatioRange, restoreRatioEnergy } from './ratioPaste';
import type { EnergyKey, PeriodKey, RatioTable as Ratios, UsageKey } from './types';

type Props = {
  value: Ratios;
} & ({
  calculated: true;
  defaults?: never;
  editing?: never;
  onChange?: never;
  onValidityChange?: never;
} | {
  calculated?: false;
  /** Référentiel, pour signaler mes écarts. */
  defaults: Ratios;
  editing: boolean;
  onChange: (next: Ratios) => void;
  onValidityChange?: (valid: boolean) => void;
});

const key = (u: UsageKey, p: PeriodKey, e: EnergyKey) => `${u}.${p}.${e}`;
const energyLabel = (e: EnergyKey) => e === 'ecs' ? 'ECS' : e === 'chauffage' ? 'Chauffage' : 'Froid';
type History = Record<EnergyKey, { past: Ratios[]; future: Ratios[] }>;
const emptyHistory = (): History => ({ chauffage: { past: [], future: [] }, ecs: { past: [], future: [] }, froid: { past: [], future: [] } });
const compactPeriod = (period: PeriodKey, label: string) => period === 'avant2005' ? 'Avant 2005'
  : period === 'p2005_2012' ? '2005–2012' : period === 'p2013_2020' ? '2013–2020'
  : period === 'apres2020' ? 'Après 2020' : label;
const shortcutsStyle: CSSProperties & { '--nj-tooltip-max-width': string } = {
  width: 'min(28rem, calc(100vw - var(--nj-semantic-size-spacing-32)))',
  '--nj-tooltip-max-width': '100%',
};
function TableToolIcon({ name, className }: { name: 'undo' | 'redo' | 'keyboard'; className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" width="24" height="24" fill="currentColor"
    aria-hidden="true" focusable="false">
    {name === 'keyboard' ? <path fillRule="evenodd"
      d="M2 5h20v14H2z M4 7v10h16V7z M5 9h2v2H5z M9 9h2v2H9z M13 9h2v2h-2z M17 9h2v2h-2z M5 12h2v2H5z M9 12h2v2H9z M13 12h2v2h-2z M17 12h2v2h-2z M7 15h10v1H7z" />
    : <g transform={name === 'redo' ? 'translate(24 0) scale(-1 1)' : undefined}>
      <path d="M3 10 10 3v5h4a8 8 0 0 1 8 8v3h-2v-3a6 6 0 0 0-6-6h-4v5z" />
    </g>}
  </svg>;
}
let clipboardToastId: string | undefined;
const clipboardToast = (paste = false) => {
  if (clipboardToastId) NJToastService.dismiss(clipboardToastId);
  clipboardToastId = NJToastService(paste ? 'Collage effectué' : 'Copie effectuée', {
    iconName: 'check_circle', hasCloseIcon: true,
    dismissAfter: 4000, gaugeLabel: 'Cette confirmation se ferme après 4 secondes.',
  });
};

/** Trois tableaux par poste : ratio de référence et coefficients indépendants par typologie × période. */
export function PerfTable({ value, defaults = value, editing = false, onChange, onValidityChange, calculated = false }: Props) {
  const id = useId();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [compactHelp, setCompactHelp] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 48rem)');
    const update = () => setCompactHelp(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const PERIODS = periodsOf(value);
  const REF_PERIOD = referencePeriodOf(value);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [activeCell, setActiveCell] = useState<string | null>(null);
  const [selection, setSelection] = useState<{ energy: EnergyKey; column: number } | null>(null);
  const [activeEnergy, setActiveEnergy] = useState<EnergyKey>('chauffage');
  const [history, setHistory] = useState<History>(emptyHistory);
  const expectedValue = useRef(value);
  useEffect(() => {
    if (value !== expectedValue.current) setHistory(emptyHistory());
    expectedValue.current = value;
  }, [value]);
  useEffect(() => { setHistory(emptyHistory()); setActiveEnergy('chauffage'); }, [editing]);
  const change = (next: Ratios, energy: EnergyKey) => {
    setHistory(current => ({ ...current, [energy]: { past: [...current[energy].past, value], future: [] } }));
    expectedValue.current = next;
    onChange?.(next);
  };
  const pendingKeys = Object.keys(texts).filter(k => k.endsWith(`.${activeEnergy}`));
  const travel = (redo = false) => {
    if (pendingKeys.length) {
      if (!redo) setTexts(current => Object.fromEntries(Object.entries(current).filter(([k]) => !pendingKeys.includes(k))));
    } else {
      const { past, future } = history[activeEnergy];
      const stack = redo ? future : past;
      const snapshot = stack[stack.length - 1];
      if (!snapshot) return;
      const next = restoreRatioEnergy(value, snapshot, activeEnergy);
      expectedValue.current = next;
      onChange?.(next);
      setHistory(current => ({ ...current, [activeEnergy]: redo
        ? { past: [...past, value], future: future.slice(0, -1) }
        : { past: past.slice(0, -1), future: [...future, value] } }));
    }
    setActiveCell(null);
  };
  useEffect(() => { if (!editing) setTexts({}); }, [editing]);
  useEffect(() => { setSelection(null); setActiveCell(null); }, [editing]);
  const hasSelection = selection !== null;
  useEffect(() => {
    if (!hasSelection) return;
    const clear = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('table.pcv3-ratio-table')) return;
      setSelection(null);
    };
    document.addEventListener('pointerdown', clear);
    return () => document.removeEventListener('pointerdown', clear);
  }, [hasSelection]);

  const invalid = useMemo(
    () => Object.entries(texts).filter(([k, t]) =>
      !(k.startsWith('ratio.') ? isValidRatio : isValidPerf)(parseNum(t))).map(([k]) => k),
    [texts],
  );
  useEffect(() => { onValidityChange?.(invalid.length === 0); }, [invalid.length, onValidityChange]);

  /** Contrôle non bloquant : les coefficients devraient décroître de gauche à droite. */
  const decreasing = useMemo(
    () => Object.fromEntries(ENERGIES.map((e) => [e.key, USAGES.every((u) =>
      PERIODS.every((p, i) => {
        const current = perfOf(value, u.key, p.key, e.key);
        const previous = i === 0 ? null : perfOf(value, u.key, PERIODS[i - 1].key, e.key);
        return current === null || previous === null || current <= previous;
      }))])),
    [value, PERIODS],
  );

  const commit = (u: UsageKey, p: PeriodKey, e: EnergyKey) => {
    const k = key(u, p, e);
    const raw = texts[k];
    if (raw === undefined) return;
    const n = parseNum(raw);
    if (!isValidPerf(n)) return;
    setTexts((t) => { const next = { ...t }; delete next[k]; return next; });
    if (n !== perfOf(value, u, p, e)) change(setPerf(value, u, p, e, n), e);
  };
  const commitRatio = (u: UsageKey, e: EnergyKey) => {
    const k = `ratio.${u}.${e}`;
    if (texts[k] === undefined) return;
    const n = parseNum(texts[k]);
    if (!isValidRatio(n)) return;
    setTexts((t) => { const next = { ...t }; delete next[k]; return next; });
    if (n !== value[u][REF_PERIOD][e]) change(setUsageRatio(value, u, e, n), e);
  };
  const destination = (element: HTMLElement, direction: 'left' | 'right' | 'up' | 'down' | 'next' | 'previous') => {
    const td = element.closest('td');
    const row = td?.parentElement;
    const table = td?.closest('table');
    if (!td || !(row instanceof HTMLTableRowElement) || !table) return undefined;
    const cells = [...table.querySelectorAll<HTMLTableCellElement>('tbody td')]
      .filter(cell => cell.querySelector('button[data-ratio-cell], input'));
    const index = cells.indexOf(td);
    if (direction === 'next' || direction === 'previous') {
      return cells[index + (direction === 'next' ? 1 : -1)];
    }
    if (direction === 'left' || direction === 'right') {
      return cells.filter(cell => cell.parentElement === row)
        .filter(cell => direction === 'left' ? cell.cellIndex < td.cellIndex : cell.cellIndex > td.cellIndex)
        [direction === 'left' ? 'pop' : 'shift']();
    }
    const targetRow = table.tBodies[0]?.rows[row.sectionRowIndex + (direction === 'down' ? 1 : -1)];
    return targetRow?.cells[td.cellIndex];
  };
  const focusCell = (target: HTMLTableCellElement | undefined, fallbackId: string) => {
    requestAnimationFrame(() => {
      const button = target?.querySelector<HTMLButtonElement>('button[data-ratio-cell]')
        ?? document.getElementById(fallbackId);
      button?.focus();
    });
  };
  const cell = (u: UsageKey, energy: EnergyKey, column: number, number: number | null, label: string) => {
    const period = column === 0 ? REF_PERIOD : PERIODS[column - 1].key;
    const k = column === 0 ? `ratio.${u}.${energy}` : key(u, period, energy);
    const cellId = `${id}.${k}`;
    const finish = () => {
      if (column === 0) commitRatio(u, energy); else commit(u, period, energy);
      if (texts[k] === undefined || (column === 0 ? isValidRatio : isValidPerf)(parseNum(texts[k]))) setActiveCell(null);
    };
    if (activeCell === k) return <NJFormItem id={cellId} size="sm" labelKind="static" labelClassName="nj-sr-only"
      className="pcv3-ratio-field" style={{ textAlign: 'center' }} autoFocus
      label={label} type="text" inputMode="decimal" hasError={invalid.includes(k)}
      value={texts[k] ?? (number === null ? '' : String(number).replace('.', ','))} placeholder="À renseigner"
      onFocus={event => { if (event.target instanceof HTMLInputElement) event.target.select(); }}
      onChange={event => {
        if (event.target instanceof HTMLInputElement) {
          const text = event.target.value;
          setTexts(current => ({ ...current, [k]: text }));
        }
      }}
      onBlur={finish}
      onKeyDown={event => {
        if ((event.key === 'Enter' || event.key === 'Tab') && event.target instanceof HTMLInputElement) {
          if (event.nativeEvent.isComposing) return;
          const valid = texts[k] === undefined || (column === 0 ? isValidRatio : isValidPerf)(parseNum(texts[k]));
          const target = destination(event.target, event.key === 'Tab'
            ? event.shiftKey ? 'previous' : 'next' : event.shiftKey ? 'up' : 'down');
          if (!valid) { event.preventDefault(); return; }
          if (event.key === 'Tab' && !target) return;
          event.preventDefault();
          finish();
          focusCell(target, `${cellId}-select`);
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setTexts(current => { const next = { ...current }; delete next[k]; return next; });
          setActiveCell(null);
          requestAnimationFrame(() => document.getElementById(`${cellId}-select`)?.focus());
        }
      }} />;
    return <NJButton id={`${cellId}-select`} variant="secondary" emphasis="minimal" scale="sm"
      className="pcv3-ratio-cell" data-ratio-cell={k} data-value={number === null ? '' : String(number).replace('.', ',')}
      aria-invalid={invalid.includes(k) || undefined}
      aria-label={`${label} : ${texts[k] ?? (number === null ? 'À renseigner' : number)}. Entrée pour modifier.`}
      label={texts[k] ?? (number === null ? 'À renseigner' : fmt(number, Number.isInteger(number) ? 0 : 2))}
      onClick={() => setSelection(null)}
      onDoubleClick={() => setActiveCell(k)}
      onKeyDown={event => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        const direction = event.key === 'ArrowLeft' ? 'left' : event.key === 'ArrowRight' ? 'right'
          : event.key === 'ArrowUp' ? 'up' : event.key === 'ArrowDown' ? 'down'
          : event.key === 'Tab' ? event.shiftKey ? 'previous' : 'next' : undefined;
        if (direction && event.target instanceof HTMLElement) {
          const target = destination(event.target, direction);
          if (event.key !== 'Tab' || target) {
            event.preventDefault();
            setSelection(null);
            focusCell(target, `${cellId}-select`);
          }
        }
        if (event.key === 'Enter' || event.key === 'F2') {
          event.preventDefault(); setActiveCell(k);
        }
      }} />;
  };
  const selected = (energy: EnergyKey, column: number) => selection?.energy === energy && (selection.column === column || selection.column === -1);
  const header = (energy: EnergyKey, column: number, label: string) =>
    <NJButton variant="secondary" emphasis="minimal" scale="sm" label={label}
        aria-label={`Sélectionner la colonne ${label} — ${energyLabel(energy)}`}
        aria-pressed={selected(energy, column)}
        data-ratio-column={column}
        onClick={() => { setSelection({ energy, column }); }}
        onKeyDown={event => { if (event.key === 'Escape') setSelection(null); }} />;

  return (
    <div className={`pcv3-energy-tables${editing ? ' pcv3-energy-editor' : ''}`} onKeyDown={event => {
      if (!editing || !(event.ctrlKey || event.metaKey) || event.altKey || event.key.toLowerCase() !== 'z') return;
      if (event.target instanceof HTMLInputElement && pendingKeys.length) return;
      if (!(event.shiftKey ? history[activeEnergy].future.length : history[activeEnergy].past.length)) return;
      event.preventDefault();
      travel(event.shiftKey);
    }}>
    <div className="pcv3-table-toolbar" role="group" aria-label="Outils des tableaux de ratios">
      <div role="tablist" aria-label={editing ? 'Poste à modifier' : 'Poste à consulter'} onKeyDown={event => {
        const index = ENERGIES.findIndex(e => e.key === activeEnergy);
        const next = event.key === 'ArrowRight' ? (index + 1) % ENERGIES.length
          : event.key === 'ArrowLeft' ? (index + ENERGIES.length - 1) % ENERGIES.length
          : event.key === 'Home' ? 0 : event.key === 'End' ? ENERGIES.length - 1 : null;
        if (next === null) return;
        event.preventDefault();
        document.getElementById(`${id}-${ENERGIES[next].key}`)?.click();
        document.getElementById(`${id}-${ENERGIES[next].key}`)?.focus();
      }}>
        {ENERGIES.map(e => <NJTab key={e.key} id={`${id}-${e.key}`} label={energyLabel(e.key)}
          isActive={activeEnergy === e.key} tabIndex={activeEnergy === e.key ? 0 : -1}
          ariaControls={`${id}-panel-${e.key}`}
          onClick={() => { setActiveEnergy(e.key); setSelection(null); setActiveCell(null); }} />)}
      </div>
      <div className="pcv3-table-history-actions pcv3-table-tools">
        {editing && <div className="pcv3-table-history-actions" onMouseDown={event => event.preventDefault()}>
          <NJTooltip text="Annuler — Ctrl/Cmd+Z" placement="bottom">
            <NJIconButton variant="tertiary" scale="sm" label="Annuler la dernière modification"
              isDisabled={!history[activeEnergy].past.length && !pendingKeys.length} onClick={() => travel()}>
              <TableToolIcon name="undo" />
            </NJIconButton>
          </NJTooltip>
          <NJTooltip text="Rétablir — Ctrl/Cmd+Maj+Z" placement="bottom">
            <NJIconButton variant="tertiary" scale="sm" label="Rétablir"
              isDisabled={!history[activeEnergy].future.length || !!pendingKeys.length} onClick={() => travel(true)}>
              <TableToolIcon name="redo" />
            </NJIconButton>
          </NJTooltip>
        </div>}
        <div onKeyDown={event => { if (event.key === 'Escape') { setShortcutsOpen(false); event.stopPropagation(); } }}>
          <NJTooltip id={`${id}-shortcuts`} placement={compactHelp ? 'bottom' : 'right'} displayed={shortcutsOpen} onDisplay={setShortcutsOpen}
            style={shortcutsStyle}
            text={<div className="pcv3-table-shortcuts"
              style={compactHelp ? { maxHeight: 'calc(50vh - var(--nj-semantic-size-spacing-48))' } : undefined}>
              <NJText as="strong" scale="sm">Raccourcis clavier</NJText>
              <div><kbd>⌘C</kbd> / <kbd>Ctrl+C</kbd><NJText as="span" scale="sm"> — copier la sélection</NJText></div>
              {editing && <>
                <div><kbd>⌘V</kbd> / <kbd>Ctrl+V</kbd><NJText as="span" scale="sm"> — coller dans la sélection</NJText></div>
                <div><kbd>Double-clic</kbd><NJText as="span" scale="sm"> — éditer la cellule</NJText></div>
                <div><kbd>← ↑ ↓ →</kbd><NJText as="span" scale="sm"> — changer de cellule</NJText></div>
                <div><kbd>Tab</kbd> / <kbd>Maj+Tab</kbd><NJText as="span" scale="sm"> — cellule suivante / précédente</NJText></div>
                <div><kbd>Entrée</kbd><NJText as="span" scale="sm"> — éditer ; en saisie, valider et descendre</NJText></div>
                <div><kbd>Maj+Entrée</kbd><NJText as="span" scale="sm"> — valider et remonter</NJText></div>
                <div><kbd>Échap</kbd><NJText as="span" scale="sm"> — annuler la saisie ou désélectionner</NJText></div>
              </>}
              <div><kbd>Clic</kbd> + <kbd>Typologie</kbd><NJText as="span" scale="sm"> — sélectionner le tableau</NJText></div>
              <div><kbd>Clic</kbd> + <kbd>En-tête colonne</kbd><NJText as="span" scale="sm"> — sélectionner la colonne</NJText></div>
              <NJText scale="xs">Sur un onglet Chauffage, ECS ou Froid, utiliser ← / → pour changer de poste.</NJText>
              {editing && <NJText scale="xs">Pendant la saisie, les flèches déplacent le curseur dans le texte.</NJText>}
            </div>}>
            <NJIconButton variant="tertiary" scale="sm" label="Raccourcis clavier"
              ariaDescribedby={`${id}-shortcuts`} ariaPressed={shortcutsOpen}
              onClick={() => setShortcutsOpen(true)}>
              <TableToolIcon name="keyboard" />
            </NJIconButton>
          </NJTooltip>
        </div>
      </div>
    </div>
    {!calculated && missingRatioCount(value) > 0 && <NJText role="status">Ratios incomplets : {missingRatioCount(value)} valeurs de poste/période à renseigner.
      Les cellules sans valeur restent à renseigner. Aucun ratio n’est déduit d’une autre catégorie.
      Les anciennes catégories Enseignement et Autre restent conservées dans les données, sans réaffectation.</NJText>}
    {ENERGIES.map((e) => (
    <section key={e.key} id={`${id}-panel-${e.key}`} hidden={activeEnergy !== e.key}
      role="tabpanel" aria-labelledby={`${id}-${e.key}`}
      aria-label={`${calculated ? 'Ratios calculés' : 'Ratios et coefficients'} — ${e.label}`}>
    <div className="pcv3-energy-table-scroll" role="region" aria-label={`Tableau ${energyLabel(e.key)}`} tabIndex={0}>
    <table className="pcv3-ratio-table" aria-label={`${energyLabel(e.key)} — ${calculated ? 'ratios calculés du projet' : 'ratios de référence et coefficients de performance'}`}
      onFocusCapture={event => { if (event.target instanceof HTMLInputElement) setSelection(null); }}
      onCopy={event => {
        if (!(event.target instanceof Element)) return;
        if (event.target instanceof HTMLInputElement) { clipboardToast(); return; }
        if (event.target.closest('button[data-ratio-table]')) {
          event.preventDefault();
          event.clipboardData.setData('text/plain', copyRatioTable(value, e.key, calculated));
          clipboardToast();
          return;
        }
        const fromCell = event.target.closest('button[data-ratio-cell]');
        if (fromCell) {
          event.preventDefault();
          event.clipboardData.setData('text/plain', fromCell.getAttribute('data-value') ?? fromCell.textContent ?? '');
          clipboardToast();
          return;
        }
        if (selection?.energy !== e.key || !event.target.closest('button[data-ratio-column]')) {
          if (window.getSelection()?.toString()) clipboardToast();
          return;
        }
        event.preventDefault();
        event.clipboardData.setData('text/plain', calculated
          ? USAGES.map(u => {
            const number = value[u.key][selection.column === 0 ? REF_PERIOD : PERIODS[selection.column - 1].key][e.key];
            return number === null ? '' : String(number).replace('.', ',');
          }).join('\n')
          : copyRatioColumn(value, e.key, selection.column));
        clipboardToast();
      }}
      onPaste={(event) => {
        if (!editing || !(event.target instanceof Element)) return;
        const fromHeader = event.target.closest('button[data-ratio-column]');
        const column = fromHeader && selection?.energy === e.key ? selection.column : undefined;
        if (column === undefined && !(event.target instanceof HTMLInputElement) && !event.target.closest('button[data-ratio-cell]')) return;
        const text = event.clipboardData.getData('text/plain');
        const cell = event.target.closest('td');
        const row = cell?.parentElement;
        if (column === undefined && (!(cell instanceof HTMLTableCellElement) || !(row instanceof HTMLTableRowElement))) return;
        event.preventDefault();
        try {
          const pasted = column !== undefined
            ? pasteRatioColumn(value, e.key, column, text)
            : row instanceof HTMLTableRowElement && cell instanceof HTMLTableCellElement
              ? pasteRatioRange(value, e.key, row.sectionRowIndex, cell.cellIndex - 1, text)
              : undefined;
          if (!pasted) return;
          change(pasted.ratios, e.key);
          setActiveCell(null);
          setTexts(current => Object.fromEntries(Object.entries(current).filter(([k]) => !pasted.keys.includes(k))));
          clipboardToast(true);
        } catch (error) {
          if (!(error instanceof Error)) throw error;
          NJToastService(error.message, { title: 'Collage refusé', iconName: 'error',
            hasCloseIcon: true, shouldDismiss: false, ariaProps: { role: 'alert', 'aria-live': 'assertive' } });
        }
      }}>
      <colgroup>
        <col className="pcv3-ratio-typology-column" />
        <col />
        {PERIODS.map((p) => <col key={p.key} />)}
      </colgroup>
      <thead>
        <tr>
          <NJText as="th" scale="sm" scope="col" data-column-selected={selected(e.key, -1) || undefined}>
            <NJButton variant="secondary" emphasis="minimal" scale="sm" label="Typologie" data-ratio-table
              aria-label={`Sélectionner tout le tableau — ${energyLabel(e.key)}`}
              aria-pressed={selected(e.key, -1)} title="Sélectionner tout le tableau, puis Ctrl/Cmd+C pour copier"
              onClick={() => setSelection({ energy: e.key, column: -1 })}
              onKeyDown={event => { if (event.key === 'Escape') setSelection(null); }} />
          </NJText>
          <NJText as="th" scale="sm" scope="col" title="Ratio en kWh/m²/an" data-column-selected={selected(e.key, 0) || undefined}>
            {header(e.key, 0, editing ? 'Ratio (kWh/m²/an)' : calculated ? 'Ratio projet de référence' : 'Ratio de référence')}{!editing && <><br />kWh/m²/an</>}</NJText>
          {PERIODS.map((p, i) => (
            <NJText as="th" scale="sm" scope="col" key={p.key} title={`${p.label}${p.key === REF_PERIOD ? ' — référence verrouillée à 100 %' : ''}`} data-column-selected={selected(e.key, i + 1) || undefined}>
              {header(e.key, i + 1, editing ? `${compactPeriod(p.key, p.label)} (%)` : p.label)}
              {p.key === REF_PERIOD && !editing && <><br />Réf. 100 %</>}
            </NJText>
          ))}
        </tr>
      </thead>
      <tbody>
        {USAGES.map((u) => (
          <tr key={u.key}>
            <NJText as="td" scale="sm" data-column-selected={selected(e.key, -1) || undefined}>{u.label}</NJText>
            <NJText as="td" scale="sm" data-calculated-ratio={calculated ? `${u.key}.${REF_PERIOD}.${e.key}` : undefined}
              data-column-selected={selected(e.key, 0) || undefined}
              className={!editing && value[u.key][REF_PERIOD][e.key] !== defaults[u.key][REF_PERIOD][e.key] ? 'pcv3-ratio-changed' : undefined}>
              {editing ? cell(u.key, e.key, 0, value[u.key][REF_PERIOD][e.key], `${u.label} — ${e.label}`)
                : value[u.key][REF_PERIOD][e.key] === null ? 'À renseigner' : fmt(value[u.key][REF_PERIOD][e.key], 1)}
            </NJText>
            {PERIODS.map((p, i) => {
              if (calculated) return <NJText as="td" scale="sm" key={p.key} data-column-selected={selected(e.key, i + 1) || undefined} data-calculated-ratio={`${u.key}.${p.key}.${e.key}`}
                className={p.key === REF_PERIOD ? 'pcv3-ratio-locked' : undefined}>
                {value[u.key][p.key][e.key] === null ? 'Indisponible' : fmt(value[u.key][p.key][e.key], 1)}
              </NJText>;
              const current = perfOf(value, u.key, p.key, e.key);
              const base = perfOf(defaults, u.key, p.key, e.key);
              const changed = current !== base;
              const locked = p.key === REF_PERIOD;
              return (
                <NJText as="td" scale="sm" key={p.key} data-column-selected={selected(e.key, i + 1) || undefined}
                  className={locked ? 'pcv3-ratio-locked' : !editing && changed ? 'pcv3-ratio-changed' : undefined}>
                  {editing && !locked ? cell(u.key, e.key, i + 1, current, `${energyLabel(e.key)} — ${u.label} — ${p.label}`) : (
                    <NJText as="span" scale="sm">
                      {current === null ? 'À renseigner' : `${fmt(current, 1)} %`}
                    </NJText>
                  )}
                </NJText>
              );
            })}
          </tr>
        ))}
      </tbody>
      {!calculated && (invalid.length > 0 || !decreasing[e.key] || USAGES.some((u) => PERIODS.some((p) => {
        const coefficient = perfOf(value, u.key, p.key, e.key);
        return coefficient !== null && !isValidPerf(coefficient);
      }))) && <tfoot>
        <tr>
          <NJText as="td" scale="sm" colSpan={PERIODS.length + 2}>
            <>
            {invalid.length > 0 && <NJText as="span" scale="sm" className="pcv3-ratios-error"> · Valeurs hors plage : ratios 0 – 1 000 kWh/m²/an ; coefficients 1 – 100 %</NJText>}
            {invalid.length === 0 && !decreasing[e.key] && <NJText as="span" scale="sm"> · certaines valeurs remontent d’une période à la suivante</NJText>}
            {USAGES.some((u) => PERIODS.some((p) => {
              const coefficient = perfOf(value, u.key, p.key, e.key);
              return coefficient !== null && !isValidPerf(coefficient);
            })) &&
              <NJText scale="sm">Certaines valeurs héritées sont hors de la plage de saisie 1–100 % ; elles sont conservées sans correction automatique.</NJText>}
            </>
          </NJText>
        </tr>
      </tfoot>}
    </table>
    </div>
    </section>
    ))}
    </div>
  );
}
